<script setup lang="ts">
import type { IconName } from "../Icon/icons";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import VDropdown from "../Dropdown/VDropdown.vue";
import VDropdownItem from "../DropdownItem/VDropdownItem.vue";
import VInput from "../Input/VInput.vue";
import VToolbarTrigger from "./VToolbarTrigger.vue";

export interface VMenuSelectItem {
  value: string | number
  name: string
  icon?: IconName | null
  /** Optional Tailwind class for a colored status/dot leading marker */
  dot?: string | null
}

const {
  items,
  multiple = false,
  defaultValue = undefined,
  tooltipText = undefined,
  buttonIcon,
  closeOnSelect,
  resetText = undefined,
  alreadyDefaultText = undefined,
  buttonText = null,
  searchable = false,
  searchPlaceholder = undefined,
  emptyText = undefined,
} = defineProps<{
  items: VMenuSelectItem[]
  multiple?: boolean
  defaultValue?: string | number | Array<string | number>
  tooltipText?: string
  buttonIcon: IconName
  /** Defaults to `!multiple` */
  closeOnSelect?: boolean
  resetText?: string
  alreadyDefaultText?: string
  /** Short value next to the trigger icon, such as the chosen option. The trigger widens to fit it. */
  buttonText?: string | null
  /** Shows a search field above the items and limits the list height, for long lists such as people. */
  searchable?: boolean
  searchPlaceholder?: string
  /** Shown instead of the items when the search matches nothing. */
  emptyText?: string
}>();

const modelValue = defineModel<string | number | Array<string | number>>({ required: true });

const { t } = useI18n();

const resolvedTooltip = computed(() => tooltipText ?? t("base.filter.tooltip"));

const resolvedResetText = computed(() => resetText ?? t("base.filter.reset"));

const alreadyDefaultTooltip = computed(
  () => alreadyDefaultText ?? t("base.filter.already_default"),
);

const shouldCloseOnSelect = computed(() => closeOnSelect ?? !multiple);

const search = ref("");

const resolvedSearchPlaceholder = computed(() => searchPlaceholder ?? t("base.search"));

const resolvedEmptyText = computed(() => emptyText ?? t("base.empty"));

const visibleItems = computed(() => {
  const query = search.value.trim().toLocaleLowerCase();
  if (!searchable || query === "") {
    return items;
  }
  return items.filter(item => item.name.toLocaleLowerCase().includes(query));
});

function valuesEqual(
  a: string | number | Array<string | number> | undefined,
  b: string | number | Array<string | number> | undefined,
): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    const left = Array.isArray(a) ? a : [];
    const right = Array.isArray(b) ? b : [];
    if (left.length !== right.length) {
      return false;
    }
    const rightSet = new Set(right.map(String));
    return left.every(value => rightSet.has(String(value)));
  }
  return a === b;
}

const showReset = computed(() => defaultValue !== undefined);

const isAtDefault = computed(() => {
  if (defaultValue === undefined) {
    return true;
  }
  return valuesEqual(modelValue.value, defaultValue);
});

const isActive = computed(() => {
  if (defaultValue === undefined) {
    return false;
  }
  return !isAtDefault.value;
});

function isSelected(value: string | number): boolean {
  if (multiple) {
    return Array.isArray(modelValue.value) && modelValue.value.some(v => String(v) === String(value));
  }
  return String(modelValue.value) === String(value);
}

function selectItem(value: string | number) {
  if (multiple) {
    const current = Array.isArray(modelValue.value) ? [...modelValue.value] : [];
    const index = current.findIndex(v => String(v) === String(value));
    if (index >= 0) {
      current.splice(index, 1);
    } else {
      current.push(value);
    }
    modelValue.value = current;
    return;
  }

  if (String(modelValue.value) === String(value)) {
    return;
  }
  modelValue.value = value;
}

function onReset() {
  if (!showReset.value || isAtDefault.value || defaultValue === undefined) {
    return;
  }
  modelValue.value = Array.isArray(defaultValue) ? [...defaultValue] : defaultValue;
}
</script>

<template>
  <VDropdown
    theme="verbleif-popper"
    placement="bottom-start"
    class="shrink-0"
    @hide="search = ''"
  >
    <template #popper="{ hide }">
      <div class="options min-w-[180px]">
        <VDropdownItem
          v-if="showReset"
          :close-on-click="!isAtDefault"
          :disabled="isAtDefault"
          :disabled-notice="alreadyDefaultTooltip"
          :text="resolvedResetText"
          icon="undo"
          :hide="hide"
          @click="onReset"
        />
        <div
          v-if="showReset"
          class="mx-2 my-1.5 border-t border-gray-200 dark:border-dark-600"
          role="separator"
        />
        <div
          v-if="searchable"
          class="px-1 pb-2"
        >
          <VInput
            v-model="search"
            icon-left="search"
            :placeholder="resolvedSearchPlaceholder"
            :aria-label="resolvedSearchPlaceholder"
            autocomplete="off"
            clearable
          />
        </div>
        <p
          v-if="searchable && visibleItems.length === 0"
          class="px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400"
        >
          {{ resolvedEmptyText }}
        </p>
        <div :class="searchable ? 'max-h-72 overflow-y-auto' : undefined">
          <VDropdownItem
            v-for="item in visibleItems"
            :key="String(item.value)"
            :close-on-click="shouldCloseOnSelect"
            :text="item.name"
            :icon="item.dot ? null : (item.icon ?? null)"
            :selected="isSelected(item.value)"
            :selection="multiple ? 'multiple' : 'single'"
            :hide="hide"
            @click="selectItem(item.value)"
          >
            <template
              v-if="item.dot"
              #icon
            >
              <span
                class="block w-2 h-2 rounded-[2px]"
                :class="item.dot"
              />
            </template>
          </VDropdownItem>
        </div>
      </div>
    </template>
    <VToolbarTrigger
      :icon="buttonIcon"
      :label="resolvedTooltip"
      :active="isActive"
      :text="buttonText"
      has-popup
    />
  </VDropdown>
</template>
